<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('microLearning.assignments.title') }}</h4>
					<p class="mb-0">{{ $t('microLearning.assignments.description') }}</p>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-3">
					<button type="button" class="btn btn-outline-secondary" @click="$router.push('/micro-learning')">
						<i class="ri-arrow-left-line me-1"></i>{{ $t('common.actions.back') }}
					</button>
				</div>
			</div>

			<div class="col-lg-12">
				<div class="card">
					<div class="card h-100">
						<div class="card-body pb-0">
							<form class="dt_adv_search">
								<div class="row g-5">
									<div class="col-12 col-sm-6 col-lg-6">
										<div class="form-floating form-floating-outline">
											<select class="form-select" v-model="filters.moduleId" @change="applyFilters">
												<option value="">{{ $t('microLearning.assignments.allModules') }}</option>
												<option v-for="mod in modulesList" :key="mod._id" :value="mod._id">{{ mod.name }}</option>
											</select>
											<label>{{ $t('microLearning.assignments.filterByModule') }}</label>
										</div>
									</div>
									<div class="col-12 col-sm-6 col-lg-6">
										<div class="form-floating form-floating-outline">
											<select class="form-select" v-model="filters.status" @change="applyFilters">
												<option value="">{{ $t('microLearning.assignments.allStatuses') }}</option>
												<option value="pending">{{ $t('microLearning.assignments.pending') }}</option>
												<option value="started">{{ $t('microLearning.assignments.started') }}</option>
												<option value="completed">{{ $t('microLearning.assignments.completed') }}</option>
											</select>
											<label>{{ $t('microLearning.assignments.filterByStatus') }}</label>
										</div>
									</div>
								</div>
							</form>
						</div>
						<div class="table-responsive">
							<table class="table table-borderless">
								<thead>
									<tr class="border-bottom">
										<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.employee') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.module') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.status') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.score') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('microLearning.assignments.date') }}</th>
										<th class="bg-transparent fs-xsmall">{{ $t('common.fields.actions') }}</th>
									</tr>
								</thead>
								<tbody class="table-border-bottom-0">
									<tr v-for="assignment in assignments" :key="assignment._id">
										<td>
											<div class="d-flex align-items-center gap-2">
												<div class="avatar avatar-sm">
													<span class="avatar-initial rounded-circle bg-label-primary">{{ initials(assignment.user) }}</span>
												</div>
												<div>
													<span class="fw-medium">{{ assignment.user.firstName }} {{ assignment.user.lastName }}</span>
													<br />
													<small class="text-muted">{{ assignment.user.email }}</small>
												</div>
											</div>
										</td>
										<td>
											<span class="fw-medium">{{ assignment.module.name }}</span>
										</td>
										<td>
											<span class="badge rounded-pill" :class="statusBadgeClass(assignment.status)">
												{{ statusLabel(assignment.status) }}
											</span>
										</td>
										<td>
											<div v-if="assignment.status === 'completed'" class="score-circle" :style="scoreCircleStyle(assignment.score)">
												<span class="score-value">{{ assignment.score }}%</span>
											</div>
											<span v-else class="text-muted">-</span>
										</td>
										<td>
											<span class="fw-medium">{{ formatDate(assignment.createdAt) }}</span>
										</td>
										<td>
											<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.viewDetails')" @click="viewDetails(assignment._id)">
												<i class="ri-eye-line ri-22px"></i>
											</a>
											<a :href="assignment.link" target="_blank" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.openLink')">
												<i class="ri-external-link-line ri-22px"></i>
											</a>
											<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('microLearning.assignments.sendSms')" @click="openSmsModal(assignment)">
												<i class="ri-message-2-line ri-22px"></i>
											</a>
											<a href="javascript:void(0);" class="btn btn-sm btn-icon btn-text-secondary rounded-pill me-1 waves-effect waves-light" data-bs-toggle="tooltip" :data-bs-title="$t('common.actions.delete')" @click="deleteAssignment(assignment._id)">
												<i class="ri-delete-bin-6-line ri-22px"></i>
											</a>
										</td>
									</tr>
									<tr v-if="assignments.length === 0 && !isLoading">
										<td colspan="6" class="text-center text-muted py-5">
											{{ $t('microLearning.assignments.noAssignments') }}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div class="row mt-5" v-if="pagination.totalEntries > pagination.limit">
							<div class="col-sm-12 col-md-6">
								<div class="dataTables_info" role="status" aria-live="polite" style="padding-left: 15px;margin: 10px;">
									{{ $t('microLearning.assignments.totalAssignments', { count: pagination.totalEntries }) }}
								</div>
							</div>
							<div class="col-sm-12 col-md-6 d-flex justify-content-end" style="padding-right: 40px;">
								<div class="pagination-container">
									<ul class="pagination">
										<li :class="['page-item', { disabled: pagination.page === 1 }]" @click="changePage(pagination.page - 1)">
											<a class="page-link"><i class="ri-arrow-left-s-line"></i></a>
										</li>
										<li v-for="page in visiblePages" :key="page" :class="['page-item', { active: page === pagination.page }]" @click="changePage(page)">
											<a class="page-link">{{ page }}</a>
										</li>
										<li :class="['page-item', { disabled: pagination.page >= pagination.totalPages }]" @click="changePage(pagination.page + 1)">
											<a class="page-link"><i class="ri-arrow-right-s-line"></i></a>
										</li>
									</ul>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<!-- SMS MODAL -->
		<div class="modal fade" id="smsModal" tabindex="-1" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-simple">
				<div class="modal-content">
					<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					<div class="modal-body p-0" v-if="!smsSent">
						<div class="text-center mb-6">
							<i class="ri-message-2-line" style="font-size: 35px; color: #666cff;"></i>
							<h4 class="mt-3 mb-2">{{ $t('microLearning.smsModal.title') }}</h4>
							<p class="text-muted small" v-if="smsAssignment">{{ smsAssignment.user.firstName }} {{ smsAssignment.user.lastName }}</p>
						</div>

						<div class="mb-4">
							<label class="form-label">{{ $t('microLearning.smsModal.phoneLabel') }}</label>
							<div class="input-group">
								<select class="form-select" style="max-width: 120px;" v-model="smsCountryCode">
									<option value="+33">🇫🇷 +33</option>
									<option value="+1">🇺🇸 +1</option>
									<option value="+44">🇬🇧 +44</option>
									<option value="+34">🇪🇸 +34</option>
									<option value="+49">🇩🇪 +49</option>
									<option value="+39">🇮🇹 +39</option>
									<option value="+32">🇧🇪 +32</option>
									<option value="+41">🇨🇭 +41</option>
									<option value="+352">🇱🇺 +352</option>
									<option value="+966">🇸🇦 +966</option>
									<option value="+212">🇲🇦 +212</option>
									<option value="+216">🇹🇳 +216</option>
									<option value="+213">🇩🇿 +213</option>
								</select>
								<input
									type="tel"
									class="form-control"
									:placeholder="$t('microLearning.smsModal.phonePlaceholder')"
									v-model="smsPhone"
									autocomplete="off"
								/>
							</div>
						</div>

						<div class="col-12 d-flex flex-wrap justify-content-center gap-4 row-gap-4">
							<button type="button" class="btn btn-primary" @click="sendSms()" :disabled="!smsPhone || isSendingSms">
								<i class="ri-send-plane-line me-1"></i>{{ $t('microLearning.smsModal.send') }}
							</button>
							<button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
								{{ $t('common.actions.cancel') }}
							</button>
						</div>
					</div>
					<div class="modal-body p-0" v-if="smsSent">
						<div class="text-center">
							<CheckIcon />
							<p class="text-muted mt-5 small">{{ $t('microLearning.smsModal.success') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";
import { Tooltip } from "bootstrap";
import CheckIcon from "@/components/CheckIcon";

export default {
	name: "MicroLearningAssignmentsList",

	components: { CheckIcon },

	data() {
		return {
			assignments: [],
			modulesList: [],
			isLoading: false,
			filters: {
				moduleId: "",
				status: "",
			},
			pagination: {
				page: 1,
				limit: 20,
				totalEntries: 0,
				totalPages: 0,
			},

			// SMS modal
			smsAssignment: null,
			smsCountryCode: "+33",
			smsPhone: "",
			isSendingSms: false,
			smsSent: false,
		};
	},

	computed: {
		visiblePages() {
			const start = Math.floor((this.pagination.page - 1) / 3) * 3 + 1;
			const end = Math.min(start + 2, this.pagination.totalPages);
			return Array.from({ length: end - start + 1 }, (_, i) => start + i);
		},
	},

	methods: {
		viewDetails(assignmentId) {
			this.$router.push(`/micro-learning/assignments/${assignmentId}`);
		},

		openSmsModal(assignment) {
			this.smsAssignment = assignment;
			this.smsCountryCode = "+33";
			this.smsPhone = assignment.user.phone || "";
			this.smsSent = false;
			this.isSendingSms = false;

			this.$nextTick(() => {
				const el = document.getElementById("smsModal");
				let modal = window.bootstrap?.Modal?.getInstance(el);
				if (!modal) {
					modal = new window.bootstrap.Modal(el);
				}
				modal.show();
			});
		},

		async sendSms() {
			if (!this.smsPhone || !this.smsAssignment) return;
			this.isSendingSms = true;

			try {
				await HTTP.post(`/micro-learning/assignments/${this.smsAssignment._id}/send-sms`, {
					phone: this.smsCountryCode + this.smsPhone,
				});
				this.smsSent = true;
				setTimeout(() => {
					const modal = window.bootstrap.Modal.getInstance(document.getElementById("smsModal"));
					if (modal) modal.hide();
				}, 1500);
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isSendingSms = false;
			}
		},

		async deleteAssignment(assignmentId) {
			if (!confirm(this.$t("microLearning.assignments.confirmDelete"))) return;

			try {
				await HTTP.delete(`/micro-learning/assignments/${assignmentId}`);
				this.fetchAssignments();
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			}
		},

		async fetchAssignments() {
			this.isLoading = true;
			try {
				const params = {
					page: this.pagination.page,
					limit: this.pagination.limit,
					status: this.filters.status || undefined,
					moduleId: this.filters.moduleId || undefined,
				};

				const response = await HTTP.get("/micro-learning/assignments", { params });
				this.assignments = response.data.assignments;

				if (response.data.pagination) {
					this.pagination.totalEntries = response.data.pagination.totalEntries;
					this.pagination.totalPages = response.data.pagination.totalPages;
				}

				this.$nextTick(() => {
					const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
					tooltipTriggerList.forEach((el) => {
						new Tooltip(el);
					});
				});
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
			} finally {
				this.isLoading = false;
			}
		},

		async fetchModulesList() {
			try {
				const response = await HTTP.get("/micro-learning/modules");
				this.modulesList = response.data.modules;
			} catch (error) {
				console.error("Erreur API:", error);
			}
		},

		changePage(newPage) {
			if (newPage > 0 && newPage <= this.pagination.totalPages) {
				this.pagination.page = newPage;
				this.fetchAssignments();
			}
		},

		applyFilters() {
			this.pagination.page = 1;
			this.fetchAssignments();
		},

		statusBadgeClass(status) {
			const map = { pending: "bg-label-primary", started: "bg-label-info", completed: "bg-label-success" };
			return map[status] || "bg-label-secondary";
		},

		statusLabel(status) {
			return this.$t(`microLearning.assignments.${status}`);
		},

		scoreColor(score) {
			if (score <= 40) return "#dc3545";
			if (score <= 70) return "#f59e0b";
			return "#10b981";
		},

		scoreCircleStyle(score) {
			const color = this.scoreColor(score);
			return {
				background: `conic-gradient(${color} ${score * 3.6}deg, #e9ecef ${score * 3.6}deg)`,
			};
		},

		scoreClass(score) {
			if (score <= 40) return "text-danger";
			if (score <= 70) return "text-warning";
			return "text-success";
		},

		initials(user) {
			const f = user.firstName ? user.firstName.charAt(0).toUpperCase() : "";
			const l = user.lastName ? user.lastName.charAt(0).toUpperCase() : "";
			return `${f}${l}`;
		},

		formatDate(dateStr) {
			if (!dateStr) return "-";
			const d = new Date(dateStr);
			return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
		},
	},

	async mounted() {
		await Promise.all([this.fetchAssignments(), this.fetchModulesList()]);
	},
};
</script>

<style scoped>
.score-circle {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
}

.score-circle::before {
	content: '';
	position: absolute;
	width: 32px;
	height: 32px;
	border-radius: 50%;
	background: #fff;
}

.score-value {
	position: relative;
	font-size: 0.7rem;
	font-weight: 600;
}
</style>
